import type { RuntimeConfig } from "@/types";

let cached: Promise<RuntimeConfig> | null = null;

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isCrossTenantOffersConfig(value: unknown): value is RuntimeConfig["crossTenantOffers"] {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    (v.mode === "off" || v.mode === "interest-only" || v.mode === "active") &&
    isNullableString(v.consentVersion) &&
    isNullableString(v.emailConsentText) &&
    isNullableString(v.smsConsentText)
  );
}

function isBusinessCampaignsConfig(value: unknown): value is RuntimeConfig["businessCampaigns"] {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return v.mode === "off" || v.mode === "active";
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isApplicationUrl(value: unknown): value is string {
  if (!isNonEmptyString(value)) return false;
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === "https:" &&
      parsed.username === "" &&
      parsed.password === "" &&
      parsed.pathname === "/" &&
      parsed.search === "" &&
      parsed.hash === "" &&
      value === parsed.origin
    );
  } catch {
    return false;
  }
}

function isRuntimeConfig(value: unknown): value is RuntimeConfig {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    isApplicationUrl(v.applicationUrl) &&
    isNonEmptyString(v.region) &&
    isNonEmptyString(v.userPoolId) &&
    isNonEmptyString(v.userPoolClientId) &&
    isCrossTenantOffersConfig(v.crossTenantOffers) &&
    isBusinessCampaignsConfig(v.businessCampaigns)
  );
}

/** Fetches and memoizes the deploy-time runtime config served at /config.json. */
export function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (!cached) {
    cached = fetch("/config.json", { cache: "no-store" })
      .catch(() => {
        throw new Error("Couldn't reach the server to load configuration.");
      })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Configuration failed to load (status ${res.status}).`);
        }
        return res.json();
      })
      .then((data) => {
        if (!isRuntimeConfig(data)) {
          throw new Error("Configuration response was missing required fields.");
        }
        return data;
      })
      .catch((err: unknown) => {
        cached = null;
        throw err instanceof Error ? err : new Error("Configuration failed to load.");
      });
  }
  return cached;
}
